import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import {
  KTSVG,
  LoadingGrid,
  Pagination,
  formatPrice,
} from '../../../../../rbts/helpers';
import { DetailConciliationModal } from '../../../conciliation/_modals/DetailConciliationModal';
import { PdfPreviewConciliationModal } from '../../../conciliation/_modals/PdfPreviewConciliationModal';
import { useGetReportConciliations } from '../hooks/ReporteConsilationHook';

type Props = {
  reloadGrid: number;
  strDate: string;
  idsDia: number[];
  idTipo: number;
};

const PAGE_SIZE = 10;

const ConciliationGrid: React.FC<Props> = ({
  reloadGrid,
  strDate,
  idsDia,
  idTipo,
}) => {
  const { conciliationsList, loadingRequest } = useGetReportConciliations(idsDia, idTipo);
  const [showModalDetail, setShowModalDetail] = useState<boolean>(false);
  const [conciliationId, setConciliationId] = useState<number>(0);
  const [showModalPdf, setShowModalPdf] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);

  const totalPages = Math.ceil(conciliationsList.length / PAGE_SIZE);
  const paginated = conciliationsList.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleDownloadExcel = async () => {
    setLoadingDownload(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Reporte Conciliación');

      ws.columns = [
        { header: 'Folio',            key: 'folio',       width: 20 },
        { header: 'Nombre del puesto', key: 'nombre',     width: 30 },
        { header: 'Proveedor',        key: 'proveedor',   width: 20 },
        { header: 'Saldo',            key: 'saldo',       width: 16 },
      ];

      ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
      ws.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      conciliationsList.forEach((row: any) => {
        ws.addRow({
          folio:     row.folio ?? 'No registrado',
          nombre:    row.punto_venta?.nombre ?? 'No registrado',
          proveedor: 'No registrado',
          saldo:     row.monto ? Number(row.total_a_pagar ?? 0) : null,
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte_conciliacion.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <>
      {loadingRequest ? (
        <LoadingGrid text="Cargando..." />
      ) : idsDia.length === 0 ? (
        <div className="text-center text-muted py-8 fs-6">
          Seleccione al menos un día administrativo para ver las conciliaciones.
        </div>
      ) : conciliationsList.length === 0 ? (
        <div className="text-center text-muted py-8 fs-6">
          No se encontraron conciliaciones para los días seleccionados.
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-sm btn-light-success"
              onClick={handleDownloadExcel}
              disabled={loadingDownload}
            >
              {loadingDownload ? (
                <span className="indicator-progress" style={{ display: 'block' }}>
                  Descargando...
                  <span className="spinner-border spinner-border-sm align-middle ms-2" />
                </span>
              ) : (
                'Descargar Excel'
              )}
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-rounded border gy-4 gs-7">
              <thead>
                <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                  <th>Folio</th>
                  <th>Nombre del puesto</th>
                  <th>Proveedor</th>
                  <th className="text-end">Saldo</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row: any) => (
                  <tr key={row.id} className="align-middle">
                    <td className="fw-bold fs-7" style={{ color: 'var(--tc-primary)' }}>
                      {row.folio ?? 'No registrado'}
                    </td>
                    <td className="fs-7">{row.punto_venta?.nombre ?? 'No registrado'}</td>
                    <td className="fs-7 text-muted">No registrado</td>
                    <td className="text-end fs-7 fw-semibold">
                      {row.monto ? (
                        <span className="tc-amount-positive">
                          {formatPrice(row.total_a_pagar ?? 0, 'es-MX', 'MXN', 2, 2)}
                        </span>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td className="text-center">
                      <a
                        className="btn btn-sm ms-2"
                        href="#/"
                        title="Descargar pdf"
                        onClick={() => {
                          setShowModalPdf(!showModalPdf);
                          setConciliationId(row.id);
                        }}
                      >
                        <KTSVG
                          path="/media/icons/duotune/files/fil029.svg"
                          className="svg-icon-1 svg-icon-primary"
                        />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </>
      )}
      <DetailConciliationModal
        strDate={strDate}
        show={showModalDetail}
        concilation={conciliationId}
        handleClose={() => {
          setShowModalDetail(false);
          setConciliationId(0);
        }}
      />
      <PdfPreviewConciliationModal
        strDate={strDate}
        show={showModalPdf}
        concilation={conciliationId}
        handleClose={() => {
          setShowModalPdf(false);
          setConciliationId(0);
        }}
      />
    </>
  );
};

export { ConciliationGrid };
